import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { useParams } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { RootState } from 'af-reducers';

import Breadcrumbs from 'af-components/Breadcrumbs';

import CLIENT from 'af-routes/client';

import * as JobTemplateActions from 'af-actions/jobTracking';

import FormModel from '../Shared/formModel';
import JobTrackingForm from '../Shared/JobTrackingForm';

interface CompanySettingsRouteProps {
	orgAlias: string;
	companyName: string;
	templateId: string;
}

type OwnProps = CustomRouteComponentProps<CompanySettingsRouteProps>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const EditJobTrackingForm: React.FC<Props> = (props: Props) => {

	const {
		company,
		edit,
		getTemplate,
	} = props;

	const params = useParams();
	const history = useHistory();
	const location = useLocation<{ orgAlias: string; }>();

	const { id: templateId } = params;

	const [template, setTemplate] = React.useState<Nullable<FormModel>>(null);

	const fetchTemplate = React.useCallback(async (id: number) => {
		const _template = await getTemplate(id);
		setTemplate(FormModel.fromVM(_template));
	}, [getTemplate]);

	React.useEffect(() => {
		fetchTemplate(+templateId!);
	}, [fetchTemplate, templateId]);

	const editTemplate = React.useCallback(async (_template: FormModel) => {
		if (!_template.id) {
			throw new Error('Invalid template');
		}

		await edit(FormModel.toEditRM(_template), +templateId!);
		history.push(CLIENT.COMPANY.SETTINGS.JOB_TRACKING_TEMPLATES.ROOT(location.state.orgAlias, company.name));

	}, [company.name, edit, history, location.state.orgAlias, templateId]);

	const goToTemplates = React.useCallback(() => {
		history.push(CLIENT.COMPANY.SETTINGS.JOB_TRACKING_TEMPLATES.ROOT(location.state.orgAlias, company.name));
	}, [company.name, history, location.state.orgAlias]);

	return (
		<div className="form-segment">
			<Breadcrumbs items={[{ label: 'Job Tracking Templates', onClick: goToTemplates }, { label: 'Edit Template' }]} />
			<JobTrackingForm initial={template ?? undefined} isEdit={true} onSubmit={editTemplate} />
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;

	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		company: companyData,
	};
}

function mapDispatchToProps() {
	return {
		edit: JobTemplateActions.editTemplate,
		getTemplate: JobTemplateActions.findById,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EditJobTrackingForm);
